'use client';

import React, { FC, useState } from 'react';
import styles from './SignUp.module.scss';
import { AuthCard } from '@/modules/auth/components/AuthCard';
import { revalidateLogic, useForm } from '@tanstack/react-form';
import { Banner, BannerProps } from '@/components/atoms/Banner';
import { Button, ButtonLink, ButtonVariants } from '@/components/atoms/Button';
import { Typographies, Typography } from '@/components/atoms/Typography';
import { TextField } from '@/components/molecules/TextField';
import { PasswordField } from '@/components/molecules/PasswordField';
import { emailValidation, getPasswordRequirements, passwordValidation } from '@/modules/auth/utils/validation';
import Link from 'next/link';
import { ROUTES } from '@/routes/constants';
import { useSignUp } from '@/modules/auth/queries';
import { useAuthErrorMessages } from '@/modules/auth/hooks/useAuthErrorMessages';
import SignUpImg from '@/assets/images/sign-up.webp';
import { extractErrorMessage } from '@/utils/error';
import { Trans, useTranslation } from 'react-i18next';

const SignUp: FC = () => {
  const { t } = useTranslation();

  const [bannerMessage, setBannerMessage] = useState<BannerProps | null>(null);

  const { mutateAsync: signUp, reset } = useSignUp();
  const getAuthErrorMessage = useAuthErrorMessages();

  const form = useForm({
    defaultValues: {
      email: '',
      password: '',
    },
    validationLogic: revalidateLogic({
      mode: 'submit',
      modeAfterSubmission: 'change',
    }),
    onSubmit: async ({ value }) => {
      try {
        reset();
        setBannerMessage(null);
        await signUp(value);
      } catch (error: unknown) {
        const errorMessage = extractErrorMessage(error);
        setBannerMessage(getAuthErrorMessage(errorMessage));
      }
    },
  });

  return (
    <AuthCard
      title={t('Sign up for Reprism')}
      quote={
        <Trans i18nKey="sign_up.quote" components={{ br: <br /> }}>
          Your AI-space for <br /> academic work
        </Trans>
      }
      img={SignUpImg}
      message={
        bannerMessage ? (
          <Banner type={bannerMessage.type} title={bannerMessage.title} text={bannerMessage.text} />
        ) : null
      }
      FormComponent={() => (
        <div>
          <form
            className={styles.form}
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
          >
            <form.Field
              name="email"
              validators={{
                onDynamic: ({ value }) => emailValidation(value, t),
              }}
            >
              {(field) => {
                return (
                  <TextField
                    label="Email"
                    placeholder={t('Enter your email')}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    error={field.state.meta.errors.length ? field.state.meta.errors[0] : ''}
                  />
                );
              }}
            </form.Field>

            <form.Field
              name="password"
              validators={{
                onChange: ({ value }) => passwordValidation(value),
              }}
            >
              {(field) => {
                return (
                  <PasswordField
                    label="Password"
                    placeholder={t('Set your password')}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    requirements={getPasswordRequirements(field.state.value, t)}
                  />
                );
              }}
            </form.Field>

            <form.Subscribe selector={(state) => state}>
              {(state) => (
                <Button
                  variant={ButtonVariants.ACCENT}
                  type="submit"
                  size="big"
                  className={styles.submitButton}
                  isFullWidth
                  disabled={!(state.isTouched && state.isValid && Object.values(state.values).every(Boolean))}
                  isLoading={state.isSubmitting}
                >
                  {t('Sign Up')}
                </Button>
              )}
            </form.Subscribe>
          </form>
          <Typography variant={Typographies.CAPTION_REGULAR} className={styles.policy}>
            <Trans i18nKey="sign_up.policy_ack">
              By continuing, you acknowledge Reprism’s{' '}
              <Link href={ROUTES.PrivacyPolicy} target="_blank" rel="noopener noreferrer">
                <span>Privacy Policy</span>
              </Link>
            </Trans>
          </Typography>
        </div>
      )}
      FooterComponent={() => (
        <footer className={styles.footer}>
          <Typography variant={Typographies.BODY_1_REGULAR}>{t('Already have an account?')}</Typography>
          <ButtonLink variant={ButtonVariants.SECONDARY} href={ROUTES.SignIn}>
            {t('Sign In')}
          </ButtonLink>
        </footer>
      )}
    />
  );
};

export { SignUp };
